import React, { useState, useMemo } from 'react';
import {
  Button,
  Link,
  Typography,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PaidIcon from '@mui/icons-material/Paid';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../modules/store/hooks';
import { marketingPersonApi, MarketingPerson, AssignmentListItem } from '../../../modules/services/marketingPersonService';
import { downloadMarketingPersonsExcel } from '../../../common/downloadMarketingPersonsExcel';
import DownloadIcon from '@mui/icons-material/Download';
import useStyles from './useStyles';
import translate from '../../../i18n/translate';
import AddMarketingPersonModal from '../AddMarketingPersonModal';
import EditMarketingPersonModal from '../EditMarketingPersonModal';
import PersonReportModal from '../PersonReportModal';
import PeriodDownloadModal from '../PeriodDownloadModal';

const MarketingPersonsScreen: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const language = useAppSelector((state) => state.auth.language);

  const prevMonthStart = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
  const prevMonthEnd = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

  const [statusFilter, setStatusFilter] = useState<string>(type || 'all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [startPeriod, setStartPeriod] = useState<string>(prevMonthStart);
  const [endPeriod, setEndPeriod] = useState<string>(prevMonthEnd);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editPerson, setEditPerson] = useState<MarketingPerson | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [mainTab, setMainTab] = useState(0);
  const [assignmentTab, setAssignmentTab] = useState<string>('all');
  const [reportSubTab, setReportSubTab] = useState<'organizer' | 'events'>('organizer');
  const [reportSearch, setReportSearch] = useState<string>('');
  const [reportStartPeriod, setReportStartPeriod] = useState<string>(prevMonthStart);
  const [reportEndPeriod, setReportEndPeriod] = useState<string>(prevMonthEnd);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [personReportOpen, setPersonReportOpen] = useState(false);
  const [personReportTarget, setPersonReportTarget] = useState<{ id: number; name: string } | null>(null);

  const statusTypes = [
    { key: 'all', title: translate('marketingPersonsAll') },
    { key: 'active', title: translate('marketingPersonsActive') },
    { key: 'unpaid', title: translate('marketingPersonsUnpaid') },
    { key: 'inactive', title: translate('marketingPersonsInactive') },
  ];

  const queryParams = useMemo(() => ({
    search: searchQuery || undefined,
    status: statusFilter,
  }), [searchQuery, statusFilter]);

  const { data: personsList = [], isLoading, refetch } = marketingPersonApi.useGetMarketingPersonsListQuery(queryParams);

  const reportFilters = useMemo(() => ({
    startPeriod: startPeriod || undefined,
    endPeriod: endPeriod || undefined,
  }), [startPeriod, endPeriod]);

  const { data: reportData = [] } = marketingPersonApi.useGetMarketingPersonsReportQuery(reportFilters, {
    skip: !startPeriod && !endPeriod,
  });

  const reportTabFilters = useMemo(() => ({
    startPeriod: reportStartPeriod || undefined,
    endPeriod: reportEndPeriod || undefined,
    search: reportSearch || undefined,
  }), [reportStartPeriod, reportEndPeriod, reportSearch]);

  const reportTabActive = !!(reportStartPeriod || reportEndPeriod || reportSearch);

  const { data: reportByOrganizerData = [], isFetching: reportByOrganizerFetching } =
    marketingPersonApi.useGetMarketingPersonsReportQuery(reportTabFilters, { skip: !reportTabActive });

  const { data: reportByEventsData = [], isFetching: reportByEventsFetching } =
    marketingPersonApi.useGetMarketingPersonsReportByEventsQuery(reportTabFilters, { skip: !reportTabActive });

  const [deletePerson] = marketingPersonApi.useDeleteMarketingPersonMutation();
  const [updateAssignment] = marketingPersonApi.useUpdateOrganizerAssignmentMutation();

  const assignmentQueryParams = useMemo(() => ({
    status: assignmentTab,
  }), [assignmentTab]);

  const { data: assignmentsList = [], isLoading: assignmentsLoading, refetch: refetchAssignments } = marketingPersonApi.useGetAssignmentsListQuery(assignmentQueryParams);

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    navigate(`/marketing-persons/${newStatus}`);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId === null) return;
    await deletePerson({
      id: deleteConfirmId,
      language: language?.split('-')?.[0] || 'en',
    });
    setDeleteConfirmId(null);
    refetch();
  };

  const handleDownloadAll = (start?: string, end?: string) => {
    downloadMarketingPersonsExcel({
      startPeriod: start,
      endPeriod: end,
    });
  };

  const handleEditClick = (person: MarketingPerson) => {
    setEditPerson(person);
    setEditModalOpen(true);
  };

  const handleAddSuccess = () => {
    setAddModalOpen(false);
    refetch();
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setEditPerson(null);
    refetch();
  };

  // Aggregate report stats
  const totalTickets = reportData.reduce((sum, r) => sum + (r.ticketCount || 0), 0);
  const totalAmount = reportData.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
  const totalFeeSum = reportData.reduce((sum, r) => sum + (r.totalFee || 0), 0);
  const totalCommission = reportData.reduce((sum, r) => sum + (r.totalCommission || 0), 0);
  const totalEvents = reportData.reduce((sum, r) => sum + (r.eventsCount || 0), 0);
  const totalReferralCommission = reportData.reduce((sum, r) => sum + (r.referralCommission || 0), 0);
  const paidReferralCommission = reportData.reduce(
    (sum, r) => sum + (r.isReferralPaid ? (r.referralCommission || 0) : 0),
    0,
  );
  const unpaidReferralCommission = Math.max(0, totalReferralCommission - paidReferralCommission);

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.4, minWidth: 60 },
    { field: 'name', headerName: translate('marketingPersonsGridName'), flex: 1.5, minWidth: 140 },
    { field: 'phone', headerName: translate('marketingPersonsGridPhone'), flex: 1, minWidth: 130 },
    { field: 'IDcode', headerName: translate('marketingPersonsGridIDcode'), flex: 1, minWidth: 110 },
    { field: 'organizersCount', headerName: translate('marketingPersonsGridOrganizersCount'), flex: 0.8, minWidth: 90 },
    {
      field: 'assignmentsCounts',
      headerName: translate('marketingPersonsGridAssignments'),
      flex: 2, minWidth: 220,
      renderCell: (params: any) => {
        const { assignmentsAll, assignmentsActive, assignmentsFinished, hasUnpaidAssignment, hasUnpaidReferral } = params.row;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <span>{translate('marketingPersonsGridAssignmentsAll')}: <b>{assignmentsAll}</b></span>
            <span style={{ color: 'green' }}>{translate('marketingPersonsGridAssignmentsActive')}: <b>{assignmentsActive}</b></span>
            <span>{translate('marketingPersonsGridAssignmentsFinished')}: <b>{assignmentsFinished}</b></span>
            {hasUnpaidAssignment && <WarningAmberIcon fontSize="small" color="error" />}
            {hasUnpaidReferral && <PaidIcon fontSize="small" color="error" />}
          </div>
        );
      },
    },
    {
      field: 'isDisabled',
      headerName: translate('marketingPersonsGridStatus'),
      flex: 0.7, minWidth: 90,
      renderCell: (params: any) => (
        <Chip
          label={params.row.isDisabled ? translate('marketingPersonsStatusDisabled') : translate('marketingPersonsStatusActive')}
          color={params.row.isDisabled ? 'default' : 'success'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: translate('marketingPersonsGridActions'),
      flex: 2.2, minWidth: 300,
      sortable: false,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <IconButton size="small" onClick={() => handleEditClick(params.row)} color="warning">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setDeleteConfirmId(params.row.id)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            color="warning"
            onClick={() => navigate(`/marketing-persons/${params.row.id}/details`)}
          >
            {translate('marketingPersonsDetailsOpen')}
          </Button>
          <Button
            size="small"
            color="warning"
            onClick={() => {
              setPersonReportTarget({ id: params.row.id, name: params.row.name });
              setPersonReportOpen(true);
            }}
          >
            {translate('marketingPersonsReport')}
          </Button>
        </div>
      ),
    },
  ];

  const reportByOrganizerColumns = [
    { field: 'marketingPersonName', headerName: translate('marketingPersonsGridName'), flex: 1.5, minWidth: 130 },
    { field: 'marketingPersonPhone', headerName: translate('marketingPersonsGridPhone'), flex: 1, minWidth: 120 },
    { field: 'organizerName', headerName: translate('marketingPersonsGridOrganizerName'), flex: 1.5, minWidth: 130 },
    { field: 'ticketCount', headerName: translate('marketingPersonsTotalTickets'), flex: 0.7, minWidth: 75 },
    {
      field: 'totalAmount',
      headerName: translate('marketingPersonsTotalAmount'),
      flex: 1, minWidth: 105,
      renderCell: (params: any) => <span>₪{(params.row.totalAmount || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalFee',
      headerName: translate('marketingPersonsGridTotalFee'),
      flex: 1, minWidth: 100,
      renderCell: (params: any) => (
        <Typography variant="body2" color="warning.main" fontWeight="bold">
          ₪{(params.row.totalFee || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'totalCommission',
      headerName: translate('marketingPersonsGridTotalCommission'),
      flex: 1, minWidth: 105,
      renderCell: (params: any) => (
        <Typography variant="body2" color="success.main" fontWeight="bold">
          ₪{(params.row.totalCommission || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'commissionType',
      headerName: translate('marketingPersonsGridCommissionType'),
      flex: 0.8, minWidth: 90,
      renderCell: (params: any) => (
        <Chip
          label={params.row.commissionType === 'percent' ? '%' : '₪'}
          size="small"
          color={params.row.commissionType === 'percent' ? 'info' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'commissionValue',
      headerName: translate('marketingPersonsGridCommissionValue'),
      flex: 0.7, minWidth: 80,
      renderCell: (params: any) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.commissionValue}{params.row.commissionType === 'percent' ? '%' : ' ₪'}
        </Typography>
      ),
    },
    { field: 'eventsCount', headerName: translate('marketingPersonsEventsCount'), flex: 0.7, minWidth: 80 },
    {
      field: 'workStartDate',
      headerName: translate('marketingPersonsGridStartDate'),
      flex: 0.8, minWidth: 95,
      renderCell: (params: any) => <span>{params.row.workStartDate ? new Date(params.row.workStartDate).toLocaleDateString() : '-'}</span>,
    },
    {
      field: 'workEndDate',
      headerName: translate('marketingPersonsGridEndDate'),
      flex: 0.8, minWidth: 95,
      renderCell: (params: any) => <span>{params.row.workEndDate ? new Date(params.row.workEndDate).toLocaleDateString() : '-'}</span>,
    },
  ];

  const reportByEventsColumns = [
    { field: 'marketingPersonName', headerName: translate('marketingPersonsGridName'), flex: 1.5, minWidth: 130 },
    { field: 'marketingPersonPhone', headerName: translate('marketingPersonsGridPhone'), flex: 1, minWidth: 120 },
    { field: 'organizerName', headerName: translate('marketingPersonsGridOrganizerName'), flex: 1.5, minWidth: 130 },
    { field: 'eventId', headerName: 'ID', flex: 0.4, minWidth: 55 },
    { field: 'eventName', headerName: translate('marketingPersonsGridEventName'), flex: 2, minWidth: 160 },
    {
      field: 'eventStartDate',
      headerName: translate('marketingPersonsGridEventDate'),
      flex: 0.8, minWidth: 95,
      renderCell: (params: any) => <span>{params.row.eventStartDate ? new Date(params.row.eventStartDate).toLocaleDateString() : '-'}</span>,
    },
    { field: 'ticketCount', headerName: translate('marketingPersonsTotalTickets'), flex: 0.7, minWidth: 75 },
    {
      field: 'totalAmount',
      headerName: translate('marketingPersonsTotalAmount'),
      flex: 1, minWidth: 100,
      renderCell: (params: any) => <span>₪{(params.row.totalAmount || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalFee',
      headerName: translate('marketingPersonsGridTotalFee'),
      flex: 1, minWidth: 95,
      renderCell: (params: any) => (
        <Typography variant="body2" color="warning.main" fontWeight="bold">
          ₪{(params.row.totalFee || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'totalCommission',
      headerName: translate('marketingPersonsGridTotalCommission'),
      flex: 1, minWidth: 100,
      renderCell: (params: any) => (
        <Typography variant="body2" color="success.main" fontWeight="bold">
          ₪{(params.row.totalCommission || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'commissionType',
      headerName: translate('marketingPersonsGridCommissionType'),
      flex: 0.8, minWidth: 90,
      renderCell: (params: any) => (
        <Chip
          label={params.row.commissionType === 'percent' ? '%' : '₪'}
          size="small"
          color={params.row.commissionType === 'percent' ? 'info' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'commissionValue',
      headerName: translate('marketingPersonsGridCommissionValue'),
      flex: 0.7, minWidth: 80,
      renderCell: (params: any) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.commissionValue}{params.row.commissionType === 'percent' ? '%' : ' ₪'}
        </Typography>
      ),
    },
  ];

  const handleToggleAssignmentPaid = async (row: AssignmentListItem) => {
    try {
      await updateAssignment({
        assignmentId: row.id,
        body: { isPaid: !row.isPaid },
        language: language?.split('-')?.[0] || 'en',
      }).unwrap();
      refetchAssignments();
    } catch (err) {
      console.error('Failed to toggle paid', err);
    }
  };

  const assignmentColumns = [
    { field: 'id', headerName: 'ID', flex: 0.4, minWidth: 55 },
    { field: 'marketingPersonName', headerName: translate('marketingPersonsGridName'), flex: 1.5, minWidth: 130 },
    { field: 'marketingPersonPhone', headerName: translate('marketingPersonsGridPhone'), flex: 1, minWidth: 120 },
    { field: 'organizerName', headerName: translate('marketingPersonsGridOrganizer'), flex: 1.5, minWidth: 130 },
    {
      field: 'commissionType',
      headerName: translate('marketingPersonsGridCommissionType'),
      flex: 0.8, minWidth: 90,
      renderCell: (params: any) => (
        <Chip
          label={params.row.commissionType === 'percent' ? '%' : '₪'}
          size="small"
          color={params.row.commissionType === 'percent' ? 'info' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'commissionValue',
      headerName: translate('marketingPersonsGridCommissionValue'),
      flex: 0.7, minWidth: 80,
      renderCell: (params: any) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.commissionValue}{params.row.commissionType === 'percent' ? '%' : ' ₪'}
        </Typography>
      ),
    },
    {
      field: 'totalAmount',
      headerName: translate('marketingPersonsTotalAmount'),
      flex: 0.9, minWidth: 100,
      renderCell: (params: any) => (
        params.row.totalAmount != null
          ? <span>₪{(params.row.totalAmount || 0).toLocaleString()}</span>
          : <span>-</span>
      ),
    },
    {
      field: 'totalFee',
      headerName: translate('marketingPersonsGridTotalFee'),
      flex: 0.9, minWidth: 95,
      renderCell: (params: any) => (
        params.row.totalFee != null
          ? <Typography variant="body2" color="warning.main" fontWeight="bold">₪{(params.row.totalFee || 0).toLocaleString()}</Typography>
          : <span>-</span>
      ),
    },
    {
      field: 'totalCommission',
      headerName: translate('marketingPersonsGridTotalCommission'),
      flex: 0.9, minWidth: 105,
      renderCell: (params: any) => (
        params.row.totalCommission != null
          ? <Typography variant="body2" color="success.main" fontWeight="bold">₪{(params.row.totalCommission || 0).toLocaleString()}</Typography>
          : <span>-</span>
      ),
    },
    {
      field: 'startDate',
      headerName: translate('marketingPersonsGridStartDate'),
      flex: 0.9, minWidth: 95,
      renderCell: (params: any) => params.row.startDate ? new Date(params.row.startDate).toLocaleDateString() : '-',
    },
    {
      field: 'endDate',
      headerName: translate('marketingPersonsGridEndDate'),
      flex: 0.9, minWidth: 95,
      renderCell: (params: any) => params.row.endDate ? new Date(params.row.endDate).toLocaleDateString() : '-',
    },
    {
      field: 'isPaid',
      headerName: translate('marketingPersonsGridPaid'),
      flex: 0.9, minWidth: 100,
      renderCell: (params: any) => (
        <Chip
          icon={<PaidIcon />}
          label={params.row.isPaid ? translate('marketingPersonsPaid') : translate('marketingPersonsUnpaid')}
          color={params.row.isPaid ? 'success' : 'error'}
          size="small"
          variant={params.row.isPaid ? 'filled' : 'outlined'}
          onClick={() => handleToggleAssignmentPaid(params.row)}
          sx={{ cursor: 'pointer' }}
        />
      ),
    },
  ];

  return (
    <div className={classes.Wrapper}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className={classes.StatusesList}>
        <Link
          color="warning.main"
          className={classes.ToMain}
          mr={10}
          ml="10px"
          onClick={() => navigate("/")}
        >
          {translate('commonBackToTheMainPage')}
        </Link>
        {statusTypes.map((item, index) => (
          <Link
            key={index}
            color="warning.main"
            style={{
              cursor: "pointer",
              textDecoration: item.key === statusFilter ? 'underline' : "none",
              fontSize: "20px",
            }}
            onClick={() => handleStatusChange(item.key)}
          >
            {item.title}
          </Link>
        ))}
        <Typography sx={{ marginInlineStart: '20px' }} fontSize={18} color='warning.main'>
          {translate('marketingPersonsTotalCount')}
          &nbsp;
          <b>{personsList.length}</b>
        </Typography>
        <Button
          color="warning"
          sx={{ marginLeft: '20px' }}
          startIcon={<SummarizeIcon />}
          onClick={() => setDownloadModalOpen(true)}
        >
          {translate("marketingPersonsDownloadAllExcel")}
        </Button>
        <Button
          color="warning"
          sx={{ marginLeft: "10px" }}
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
        >
          {translate("marketingPersonsAddPerson")}
        </Button>
      </div>

      <Tabs
        value={mainTab}
        onChange={(_, v) => {
          if (v === 1) {
            navigate('/marketing-persons/payouts');
            return;
          }
          if (v === 2) {
            navigate('/marketing-persons/payments');
            return;
          }
          setMainTab(v);
        }}
        sx={{ mt: 1, mb: 2, ml: 1, '& .MuiTab-root': { color: 'warning.main' }, '& .Mui-selected': { color: 'warning.dark' } }}
        TabIndicatorProps={{ sx: { backgroundColor: 'warning.main' } }}
      >
        <Tab label={translate('marketingPersonsTabPersons')} />
        <Tab label="Payouts" />
        <Tab label={translate('headerOpenMarketingPayments')} />
      </Tabs>

      {mainTab === 0 && (
      <div style={{ position: 'relative', width: '100%', paddingTop: '10px' }}>
        <div className={classes.FilterRow}>
          <TextField
            label={translate('marketingPersonsSearch')}
            helperText={translate('marketingPersonsSearchHelper')}
            size="small"
            color="warning"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <DatePicker
            label={translate('marketingPersonsPeriodStart')}
            value={startPeriod ? dayjs(startPeriod) : null}
            onChange={(value: Dayjs | null) => setStartPeriod(value ? value.format('YYYY-MM-DD') : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                color="warning"
              />
            )}
          />
          <DatePicker
            label={translate('marketingPersonsPeriodEnd')}
            value={endPeriod ? dayjs(endPeriod) : null}
            onChange={(value: Dayjs | null) => setEndPeriod(value ? value.format('YYYY-MM-DD') : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                color="warning"
              />
            )}
          />
          {(searchQuery || startPeriod || endPeriod) && (
            <Tooltip title={translate('marketingPersonsClearFilters')}>
              <IconButton size="small" color="warning" onClick={() => {
                setSearchQuery('');
                setStartPeriod('');
                setEndPeriod('');
              }}>
                <FilterAltOffIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {(startPeriod || endPeriod) && reportData.length > 0 && (
          <div className={classes.ReportSection}>
            <Typography variant="h6" color="warning.main">{translate('marketingPersonsReportSummary')}</Typography>
            <div className={classes.ReportStats}>
              <div className={classes.StatCard}>
                <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalTickets')}</Typography>
                <Typography variant="h6" color="warning.main">{totalTickets}</Typography>
              </div>
              <div className={classes.StatCard}>
                <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalAmount')}</Typography>
                <Typography variant="h6" color="warning.main">₪{totalAmount.toLocaleString()}</Typography>
              </div>
              <div className={classes.StatCard}>
                <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalFee')}</Typography>
                <Typography variant="h6" color="warning.main">₪{totalFeeSum.toLocaleString()}</Typography>
              </div>
              <div className={classes.StatCard}>
                <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalCommission')}</Typography>
                <Typography variant="h6" color="success.main">₪{totalCommission.toLocaleString()}</Typography>
              </div>
              <div className={classes.StatCard}>
                <Typography variant="body2" color="textSecondary">{translate('marketingPersonsEventsCount')}</Typography>
                <Typography variant="h6" color="warning.main">{totalEvents}</Typography>
              </div>
              <div className={classes.StatCard}>
                <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalReferralCommission')}</Typography>
                <Typography variant="h6" color="success.main">₪{totalReferralCommission.toLocaleString()}</Typography>
              </div>
              <div className={classes.StatCard}>
                <Typography variant="body2" color="textSecondary">{translate('marketingPersonsPaidReferralCommission')}</Typography>
                <Typography variant="h6" color="success.main">₪{paidReferralCommission.toLocaleString()}</Typography>
              </div>
              <div className={classes.StatCard}>
                <Typography variant="body2" color="textSecondary">{translate('marketingPersonsUnpaidReferralCommission')}</Typography>
                <Typography variant="h6" color="error.main">₪{unpaidReferralCommission.toLocaleString()}</Typography>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <CircularProgress color="warning" />
        ) : (
          <DataGrid
            sx={{
              backgroundColor: '#ede9e4',
              borderRadius: '0px',
              border: 'none',
              '& .MuiDataGrid-virtualScroller': {
                marginTop: '0 !important'
              },
              '& .MuiDataGrid-main': {
                overflow: 'visible'
              },
              '.MuiDataGrid-columnHeaders': {
                backgroundColor: 'warning.main',
                position: 'sticky',
                zIndex: 2,
                borderRadius: '0px',
                border: '0px'
              },
              '.MuiDataGrid-columnHeaderTitleContainer': {
                color: '#fff',
                fontSize: '15px',
                padding: '10px',
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center'
              },
              '.MuiDataGrid-cell': {
                border: `1px solid #fc7f03`,
                padding: '5px',
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center'
              },
              '.MuiDataGrid-footerContainer': {
                border: `1px solid #fc7f03`
              },
              '.MuiDataGrid-footerContainer p': {
                margin: '0px'
              },
              '.MuiDataGrid-columnHeaderTitle': {
                overflow: 'visible',
                lineHeight: '1.43rem',
                whiteSpace: 'normal'
              }
            }}
            rows={personsList}
            columns={columns as GridColDef[]}
            getRowHeight={() => 'auto'}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            density='comfortable'
          />
        )}
      </div>
      )}

      {mainTab === 1 && (
      <div style={{ position: 'relative', width: '100%', paddingTop: '10px' }}>
        <Tabs
          value={assignmentTab}
          onChange={(_, v) => setAssignmentTab(v)}
          sx={{ mb: 1, '& .MuiTab-root': { color: 'warning.main' }, '& .Mui-selected': { color: 'warning.dark' } }}
          TabIndicatorProps={{ sx: { backgroundColor: 'warning.main' } }}
        >
          <Tab label={translate('marketingPersonsAssignmentsAll')} value="all" />
          <Tab label={translate('marketingPersonsAssignmentsActive')} value="active" />
          <Tab label={translate('marketingPersonsAssignmentsUnpaid')} value="unpaid" />
          <Tab label={translate('marketingPersonsAssignmentsPaid')} value="paid" />
        </Tabs>

        {assignmentsLoading ? (
          <CircularProgress color="warning" />
        ) : (
          <DataGrid
            sx={{
              backgroundColor: '#ede9e4',
              borderRadius: '0px',
              border: 'none',
              '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
              '& .MuiDataGrid-main': { overflow: 'visible' },
              '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', position: 'sticky', zIndex: 2, borderRadius: '0px', border: '0px' },
              '.MuiDataGrid-columnHeaderTitleContainer': { color: '#fff', fontSize: '15px', padding: '10px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
              '.MuiDataGrid-cell': { border: '1px solid #fc7f03', padding: '5px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
              '.MuiDataGrid-footerContainer': { border: '1px solid #fc7f03' },
              '.MuiDataGrid-footerContainer p': { margin: '0px' },
              '.MuiDataGrid-columnHeaderTitle': { overflow: 'visible', lineHeight: '1.43rem', whiteSpace: 'normal' },
            }}
            rows={assignmentsList}
            columns={assignmentColumns as GridColDef[]}
            getRowHeight={() => 'auto'}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            density='comfortable'
          />
        )}
      </div>
      )}

      {mainTab === 2 && (
      <div style={{ position: 'relative', width: '100%', paddingTop: '10px' }}>
        <div className={classes.FilterRow}>
          <TextField
            label={translate('marketingPersonsReportSearch')}
            size="small"
            color="warning"
            value={reportSearch}
            onChange={(e) => setReportSearch(e.target.value)}
            sx={{ minWidth: 250 }}
          />
          <DatePicker
            label={translate('marketingPersonsPeriodStart')}
            value={reportStartPeriod ? dayjs(reportStartPeriod) : null}
            onChange={(value: Dayjs | null) => setReportStartPeriod(value ? value.format('YYYY-MM-DD') : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                color="warning"
              />
            )}
          />
          <DatePicker
            label={translate('marketingPersonsPeriodEnd')}
            value={reportEndPeriod ? dayjs(reportEndPeriod) : null}
            onChange={(value: Dayjs | null) => setReportEndPeriod(value ? value.format('YYYY-MM-DD') : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                color="warning"
              />
            )}
          />
          <Button
            color="warning"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => downloadMarketingPersonsExcel({
              startPeriod: reportStartPeriod || undefined,
              endPeriod: reportEndPeriod || undefined,
            })}
            disabled={!reportTabActive}
          >
            {translate('marketingPersonsDownloadAllExcel')}
          </Button>
          {(reportSearch || reportStartPeriod || reportEndPeriod) && (
            <Tooltip title={translate('marketingPersonsClearFilters')}>
              <IconButton size="small" color="warning" onClick={() => {
                setReportSearch('');
                setReportStartPeriod('');
                setReportEndPeriod('');
              }}>
                <FilterAltOffIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {reportTabActive && (reportByOrganizerData.length > 0 || reportByEventsData.length > 0) && (() => {
          type StatRow = { ticketCount: number; totalAmount: number; totalFee: number; totalCommission: number };
          const activeData: StatRow[] = reportSubTab === 'organizer' ? reportByOrganizerData : reportByEventsData;
          const totalTickets = activeData.reduce((s, r) => s + (r.ticketCount || 0), 0);
          const totalAmount = activeData.reduce((s, r) => s + (r.totalAmount || 0), 0);
          const totalFee = activeData.reduce((s, r) => s + (r.totalFee || 0), 0);
          const totalCommission = activeData.reduce((s, r) => s + (r.totalCommission || 0), 0);
          return (
            <div className={classes.ReportSection}>
              <Typography variant="h6" color="warning.main">{translate('marketingPersonsReportSummary')}</Typography>
              <div className={classes.ReportStats}>
                <div className={classes.StatCard}>
                  <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalTickets')}</Typography>
                  <Typography variant="h6" color="warning.main">{totalTickets}</Typography>
                </div>
                <div className={classes.StatCard}>
                  <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalAmount')}</Typography>
                  <Typography variant="h6" color="warning.main">₪{totalAmount.toLocaleString()}</Typography>
                </div>
                <div className={classes.StatCard}>
                  <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalFee')}</Typography>
                  <Typography variant="h6" color="warning.main">₪{totalFee.toLocaleString()}</Typography>
                </div>
                <div className={classes.StatCard}>
                  <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalCommission')}</Typography>
                  <Typography variant="h6" color="success.main">₪{totalCommission.toLocaleString()}</Typography>
                </div>
              </div>
            </div>
          );
        })()}

        <Tabs
          value={reportSubTab}
          onChange={(_, v) => setReportSubTab(v)}
          sx={{ mt: 1, mb: 1, '& .MuiTab-root': { color: 'warning.main' }, '& .Mui-selected': { color: 'warning.dark' } }}
          TabIndicatorProps={{ sx: { backgroundColor: 'warning.main' } }}
        >
          <Tab label={translate('marketingPersonsReportByOrganizer')} value="organizer" />
          <Tab label={translate('marketingPersonsReportByEvents')} value="events" />
        </Tabs>

        {!reportTabActive ? (
          <Typography color="textSecondary" sx={{ mt: 2, ml: 1 }}>
            {translate('marketingPersonsReportNoData')}
          </Typography>
        ) : reportSubTab === 'organizer' ? (
          reportByOrganizerFetching ? <CircularProgress color="warning" /> : (
            <DataGrid
              sx={{
                backgroundColor: '#ede9e4', borderRadius: '0px', border: 'none',
                '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
                '& .MuiDataGrid-main': { overflow: 'visible' },
                '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', position: 'sticky', zIndex: 2, borderRadius: '0px', border: '0px' },
                '.MuiDataGrid-columnHeaderTitleContainer': { color: '#fff', fontSize: '15px', padding: '10px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
                '.MuiDataGrid-cell': { border: '1px solid #fc7f03', padding: '5px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
                '.MuiDataGrid-footerContainer': { border: '1px solid #fc7f03' },
                '.MuiDataGrid-footerContainer p': { margin: '0px' },
                '.MuiDataGrid-columnHeaderTitle': { overflow: 'visible', lineHeight: '1.43rem', whiteSpace: 'normal' },
              }}
              rows={reportByOrganizerData.map((r, i) => ({ ...r, id: `${r.marketingPersonId}-${r.organizerId}-${i}` }))}
              columns={reportByOrganizerColumns as unknown as GridColDef[]}
              getRowHeight={() => 'auto'}
              rowsPerPageOptions={[10, 25, 50]}
              autoHeight
              density="comfortable"
            />
          )
        ) : (
          reportByEventsFetching ? <CircularProgress color="warning" /> : (
            <DataGrid
              sx={{
                backgroundColor: '#ede9e4', borderRadius: '0px', border: 'none',
                '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
                '& .MuiDataGrid-main': { overflow: 'visible' },
                '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', position: 'sticky', zIndex: 2, borderRadius: '0px', border: '0px' },
                '.MuiDataGrid-columnHeaderTitleContainer': { color: '#fff', fontSize: '15px', padding: '10px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
                '.MuiDataGrid-cell': { border: '1px solid #fc7f03', padding: '5px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
                '.MuiDataGrid-footerContainer': { border: '1px solid #fc7f03' },
                '.MuiDataGrid-footerContainer p': { margin: '0px' },
                '.MuiDataGrid-columnHeaderTitle': { overflow: 'visible', lineHeight: '1.43rem', whiteSpace: 'normal' },
              }}
              rows={reportByEventsData.map((r, i) => ({ ...r, id: `${r.marketingPersonId}-${r.eventId}-${i}` }))}
              columns={reportByEventsColumns as unknown as GridColDef[]}
              getRowHeight={() => 'auto'}
              rowsPerPageOptions={[10, 25, 50]}
              autoHeight
              density="comfortable"
            />
          )
        )}
      </div>
      )}

      <AddMarketingPersonModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => { handleAddSuccess(); refetchAssignments(); }}
      />

      {editPerson && (
        <EditMarketingPersonModal
          open={editModalOpen}
          onClose={() => { setEditModalOpen(false); setEditPerson(null); }}
          onSuccess={() => { handleEditSuccess(); refetchAssignments(); }}
          person={editPerson}
        />
      )}

      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle color="warning.main">{translate('marketingPersonsConfirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{translate('marketingPersonsConfirmDeleteText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} color="warning">{translate('marketingPersonsCancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">{translate('marketingPersonsDelete')}</Button>
        </DialogActions>
      </Dialog>

      {personReportTarget && (
        <PersonReportModal
          open={personReportOpen}
          onClose={() => { setPersonReportOpen(false); setPersonReportTarget(null); }}
          personId={personReportTarget.id}
          personName={personReportTarget.name}
        />
      )}

      <PeriodDownloadModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={handleDownloadAll}
      />
      </LocalizationProvider>
    </div>
  );
};

export default MarketingPersonsScreen;
